import React from 'react';
import {
  PanelLeft,
  Zap,
  Home,
  Compass,
  Coins,
  BookOpen,
  CalendarDays,
  Inbox,
  Settings,
  // ⚠️ Aliased: a bare `Map` would shadow the JS built-in in this module.
  Map as RoadmapIcon } from 'lucide-react';
import { AIAssistantIcon } from '../icons/AIAssistantIcon';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useReveal } from '../../hooks/useReveal';
import { useCurrentView } from '../../hooks/useCurrentView';
import { useLanguageStore } from '../../stores/languageStore';
import { useAIChatStore } from '../../stores/aiChatStore';
import { useNavDrawer } from '../../stores/navDrawerStore';
import { useGuildModeStore } from '../../stores/guildModeStore';
import type { View } from '../../lib/viewRouter';
import { UserProgress } from './UserProgress';
import { GuildRail } from './GuildRail';
import { NavDrawer, type NavDestination } from './NavDrawer';
import { PageGuideButton } from '../guide/PageGuideButton';
import { ACTION_VIEWS } from '../views/ActionShell';
import { useWalletStore } from '../../stores/walletStore';
import { formatCoinFull } from '../../utils/coin';
import { CoinAmount } from '../CoinAmount';

/**
 * Sidebar display mode — persisted so the choice survives reloads.
 *
 * ⭐ THREE modes, not a boolean: `hover` is a real third state (collapsed at
 * rest, expanded while the pointer is over the rail), and a boolean cannot hold
 * it. Exported so the Header can offset the centred session widget.
 */
export type SidebarMode = 'expanded' | 'collapsed' | 'hover';

export const SIDEBAR_MODES: { id: SidebarMode; label: string }[] = [
  { id: 'expanded', label: 'Expanded' },
  { id: 'collapsed', label: 'Collapsed' },
  { id: 'hover', label: 'Expand on hover' },
];

export const useSidebarStore = create<{
  mode: SidebarMode;
  /** ⚠️ Transient — deliberately NOT persisted; a stored hover state would make
   *  the rail open on load for a pointer that is nowhere near it. */
  hovering: boolean;
  /**
   * ⚠️⚠️ Transient. The control's own menu is 176px wide and the rail is 64px
   * when collapsed, with `overflow-hidden` — so the menu is CLIPPED unless the
   * rail widens to hold it. Persisting this would reopen the app wide because a
   * menu was open when it was last closed.
   */
  menuOpen: boolean;
  setMode: (mode: SidebarMode) => void;
  setHovering: (hovering: boolean) => void;
  setMenuOpen: (menuOpen: boolean) => void;
  toggle: () => void;
}>()(
  persist(
    (set) => ({
      mode: 'expanded' as SidebarMode,
      hovering: false,
      menuOpen: false,
      setMode: (mode: SidebarMode) => set({ mode }),
      setHovering: (hovering: boolean) => set({ hovering }),
      setMenuOpen: (menuOpen: boolean) => set({ menuOpen }),
      // Kept for the keyboard/legacy path: expanded <-> collapsed only.
      toggle: () => set((s: { mode: SidebarMode }) => ({
        mode: (s.mode === 'collapsed' ? 'expanded' : 'collapsed') as SidebarMode,
      })),
    }),
    {
      name: 'sidebar-storage',
      version: 2,
      // ⚠️ `hovering` must never round-trip.
      partialize: (s) => ({ mode: s.mode }) as any,
      /**
       * ⚠️⚠️ v1 stored `{ isCollapsed: boolean }`. Without this migrate, everyone
       * who had collapsed the rail reopens on an `undefined` mode — the same
       * class of bug as the `view-storage` v2 rename, where a persisted value
       * outlived the shape that produced it.
       */
      migrate: (persisted: any, version: number) => {
        if (version < 2) {
          return { mode: persisted?.isCollapsed ? 'collapsed' : 'expanded' };
        }
        return persisted;
      },
    }
  )
);

/**
 * ⭐ The ONE definition of "is the rail narrow right now". Derived, never stored
 * — a second copy in state would drift from `mode` the first time hover changed.
 */
export const selectSidebarCollapsed = (
  s: { mode: SidebarMode; hovering: boolean; menuOpen?: boolean },
): boolean => {
  // ⭐ An open menu forces the rail wide whatever the mode says — the menu is
  // wider than a collapsed rail and `overflow-hidden` would cut it in half.
  // Handled HERE rather than at the nav element so every consumer of
  // "is the rail narrow" agrees, including the Header's centring offset.
  if (s.menuOpen) return false;
  return s.mode === 'collapsed' || (s.mode === 'hover' && !s.hovering);
};

/**
 * ⭐⭐ THE RAIL IS THE MAP AGAIN (owner, 2026-09-29), now that the phone gets a
 * build of its own. Four pages that had been moved off the rail to live inside
 * other pages came back, each where a reader looks for it:
 *   · Roadmap  → never gated. It is where onboarding lands and where the
 *                Growth Module lives. Home's Journey panel, its old only door,
 *                is gone. (2026-10-16: it now leads the SECOND group, above
 *                Calendar — Home tops the rail.)
 *   · Calendar, Inbox → a second group, each revealed for the step that uses it.
 *   · Settings → pinned at the foot of the rail; off the avatar menu.
 * Home is GATED again (`nav:dashboard` → step 4, with the Home components),
 * which is only safe because the Roadmap no longer needs Home as its door —
 * see `constants/moduleReveal.ts` and `LANDING_VIEW`.
 * [[a-tab-can-be-its-own-only-route]]
 */
/* ⭐⭐ REORDERED (owner, 2026-10-16): Home tops the rail, and the Roadmap leads the
   second group, above the Calendar. The Roadmap is still never gated and still
   where a new user lands (`LANDING_VIEW`); before Home is revealed it is simply the
   first item, with no separator above it. */
const homeItem: NavDestination = { id: 'dashboard', icon: Home, label: 'Home' };
const roadmapItem: NavDestination = { id: 'roadmap', icon: RoadmapIcon, label: 'Roadmap' };

const primaryItems: NavDestination[] = [
  homeItem,
  { id: 'action', icon: Zap, label: 'Build' },
  /* ⛔ `domains` (the Domains page) RETIRED 2026-10-03 (owner) — every domain and
     its areas live in Roadmap → Domain Progression, where step 17 now sends you. */
  { id: 'discover', icon: Compass, label: 'Discover' },
  // ⚠️ The VIEW is `journal`; the destination is called Reflect. The id is the
  // stored key (it is in the URL and in `view-storage`), the label is display.
  { id: 'journal', icon: BookOpen, label: 'Reflect' },
];

/** The Roadmap and the planning pages — below the daily loop, above the assistant. */
const secondaryItems: NavDestination[] = [
  roadmapItem,
  { id: 'calendar', icon: CalendarDays, label: 'Calendar' },
  { id: 'inbox', icon: Inbox, label: 'Inbox' },
];

/** Pinned to the foot of the rail, beside the collapse control. Never gated. */
const settingsItem: NavDestination = { id: 'settings', icon: Settings, label: 'Settings' };

/**
 * Every destination the rail offers, exported so nothing else has to restate them.
 *
 * ⚠️⚠️ THE RAIL HIDING A DESTINATION DOES NOT STOP THE APP RENDERING IT. `currentView`
 * is restored from `view-storage` and from the URL, neither of which knows about
 * reveal — so a signed-in user landed on Connect while the rail beside it showed
 * only Home. `App` guards the rendered view against this list; keeping the list
 * here means a destination cannot be added to the rail and forgotten by the guard.
 *
 * ⚠️ `roadmap` and `settings` are in it and have no reveal step, so the guard's
 * question about them fails OPEN to `true` — which is the right answer for both.
 */
export const PRIMARY_DESTINATION_IDS: readonly View[] = [
  ...primaryItems, ...secondaryItems, settingsItem,
].map(i => i.id);

/**
 * The mobile bottom bar holds exactly 4 tabs (+ the centre AI button).
 * ⭐ The first slot is HOME ONCE REVEALED, THE ROADMAP UNTIL THEN (owner,
 * 2026-09-29) — a stopgap until the phone's own build replaces this bar. The
 * slot is picked, not filtered, so a new user's bar never loses its first tab.
 * Everything else, the Roadmap included once Home arrives, is in the drawer.
 */
const bottomBarTail: NavDestination[] = [
  { id: 'action', icon: Zap, label: 'Build' },
  { id: 'discover', icon: Compass, label: 'Discover' },
  { id: 'journal', icon: BookOpen, label: 'Reflect' },
];

// Mobile drawer lists every destination as a full side-nav menu.
const drawerItems: NavDestination[] = [...primaryItems, ...secondaryItems, settingsItem];

/**
 * ⭐ Progressive reveal filter. A destination the user has not reached yet is
 * ABSENT from the rail — never disabled, never a lock icon, and still reachable
 * by URL.
 *
 * ⚠️⚠️ `nav:roadmap` is unmapped, so the Roadmap always survives this — it is the
 * way in to the module (see `primaryItems`). A surface absent from the
 * `RevealSurface` union is always visible; that is the mechanism, and it is why
 * the Roadmap needs no special case here. Home (`nav:dashboard`) IS mapped now.
 *
 * ⚠️⚠️ `nav:journal` WAS unmapped for the same reason and IS NOW MAPPED (owner,
 * 2026-08-21) — Reflect arrives at step 10, for the step that asks for a journal
 * entry. This comment used to claim the rail was "its only door", which was
 * never true of a user in the module: seven steps carry `surface: 'journal'` and
 * open the page with their own button.
 *
 * ⚠️ THE ID IS THE VIEW ID. This builds `nav:${i.id}`, and Reflect's item id is
 * `journal` — a map entry written as `nav:reflect` would be a string nothing
 * queries, and it would fail OPEN rather than error.
 */
function useVisibleNav(items: NavDestination[]): NavDestination[] {
  const revealed = useReveal();
  return items.filter(i => revealed(`nav:${i.id}`));
}

// A nav item is active when currentView matches it — OR, for the Action item,
// when currentView is ANY view in the Action group (Quests, Habits, Tasks, …)
// so the nav Action stays highlighted across all its tabs.
function isItemActive(itemId: View, currentView: View): boolean {
  if (itemId === 'action') return ACTION_VIEWS.has(currentView);
  return currentView === itemId;
}

export function Navigation() {
  const { currentView, setCurrentView } = useCurrentView();
  const mode = useSidebarStore((s) => s.mode);
  const setMode = useSidebarStore((s) => s.setMode);
  const setHovering = useSidebarStore((s) => s.setHovering);
  const isCollapsed = useSidebarStore(selectSidebarCollapsed);
  const { direction } = useLanguageStore();
  const { isOpen: drawerOpen, close: closeDrawer } = useNavDrawer();
  const { isOpen: aiOpen, setOpen: setAIOpen } = useAIChatStore();
  // Inside a guild the rail becomes the GUILD's navigation (crest, guild
  // progress, modules) — the app shell and geometry stay identical.
  const inGuildMode = useGuildModeStore((s) => s.activeGuildId !== null);

  // ⭐ Reveal filters. Applied to all three presentations (desktop rail, mobile
  // bottom bar, drawer) — a destination hidden on one and present on another is
  // worse than either, and the bottom bar is the one people actually use.
  const revealed = useReveal();
  const visiblePrimary = useVisibleNav(primaryItems);
  const visibleSecondary = useVisibleNav(secondaryItems);
  const visibleBottomBar = useVisibleNav([
    // ⚠️ By NAME, never by index — the rail's order changed (2026-10-16), and an
    // index would have swapped Home and the Roadmap here without a sound.
    revealed('nav:dashboard') ? homeItem : roadmapItem,
    ...bottomBarTail,
  ]);
  const visibleDrawer = useVisibleNav(drawerItems);

  const go = (id: View) => setCurrentView(id);
  const active = (id: View) => isItemActive(id, currentView);

  const railButton = ({ id, icon: Icon, label }: NavDestination) => {
    const isActive = active(id);
    return (
      <button
        key={id}
        type="button"
        onClick={() => go(id)}
        aria-current={isActive ? 'page' : undefined}
        title={isCollapsed ? label : undefined}
        className={`flex items-center gap-3 w-full px-3 py-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-card ${
          isActive ? 'bg-primary/10 text-primary nav-active-contrast accent-gradient-border' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        }`}
      >
        <Icon className="w-5 h-5 shrink-0" />
        {!isCollapsed && (
          <span className={`nav-label-in text-sm truncate ${isActive ? 'accent-gradient-text' : ''}`}>{label}</span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Desktop side navigation — all destinations live here (no "More") */}
      <nav
        aria-label="Primary"
        // ⚠️ Hover is reported unconditionally; `selectSidebarCollapsed` decides
        // whether it MEANS anything. Gating the handler on the mode would leave
        // a stale `hovering: true` behind when the mode changes mid-hover.
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        // ⚠️ `transition-[width]`, NOT `transition-all`: the latter also animates
        // colour, border and shadow on every state change, which is what made
        // the collapse read as a lurch rather than a slide. One property, one
        // curve. ⚠️ `ease-in-out` over the default `ease` — the rail moves a long
        // way (128px) and a symmetric curve reads as deliberate at that distance.
        className={`hidden lg:flex flex-col bg-card border border-border rounded-xl overflow-hidden ml-4 mt-6 mb-6 transition-[width] duration-300 ease-in-out ${
          isCollapsed ? 'w-16' : 'w-48'
        }`}
      >

        {inGuildMode ? (
          <GuildRail isCollapsed={isCollapsed} />
        ) : (
          <>
            {/* ⚠️ The avatar block is the ONLY route to Status — the rail has
                no Status entry — so hiding it hides the page too, which is the
                intent (Status arrives at step 9). */}
            {/* ⭐ ONE identity block: avatar · level · rank · balance, closed by a
                SINGLE divider underneath. The wallet belongs above that line —
                it answers "what do I have", the same question as the rest of
                the block. ⚠️ The divider is drawn HERE, not by UserProgress, or
                it would cut the block in half.
                ⚠️ Each child is revealed independently (avatar at step 9,
                wallet at step 6), so the wrapper must not draw a divider when
                BOTH are hidden — an empty bordered strip. */}
            {(revealed('nav:avatar') || revealed('nav:wallet')) && (
              <div className="shrink-0 border-b border-border pb-2">
                {revealed('nav:avatar') && (
                  <UserProgress isCollapsed={isCollapsed} enableReward showDivider={false} />
                )}
                {/* px-2 here rather than a margin on the button — see w-full above. */}
                {revealed('nav:wallet') && (
                  <div className="px-2">
                    <WalletRailWidget isCollapsed={isCollapsed} />
                  </div>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {visiblePrimary.map(railButton)}

              {/* ⚠️ The line separates two groups — with nothing above it (a new user,
                  before Home), the Roadmap leads the rail with no line over it. */}
              {visiblePrimary.length > 0 && visibleSecondary.length > 0 && (
                <div role="separator" className="mx-2 my-2 border-t border-border" />
              )}
              {visibleSecondary.map(railButton)}

              {/* AI assistant — toggles the chat panel (replaces the floating orb) */}
              {revealed('nav:assistant') && <button
                type="button"
                onClick={() => setAIOpen(!aiOpen)}
                aria-pressed={aiOpen}
                title={isCollapsed ? 'AI Assistant' : undefined}
                className={`flex items-center gap-3 w-full px-3 py-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-card ${
                  aiOpen ? 'bg-primary/10 text-primary nav-active-contrast accent-gradient-border' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
              >
                {/* ⭐ An AI icon, not the brand orb (owner, 2026-10-05) — the logo
                    belongs to Metanoia, not to one feature inside it. */}
                <AIAssistantIcon className="w-5 h-5 shrink-0" />
                {!isCollapsed && (
                  <span className={`nav-label-in text-sm truncate ${aiOpen ? 'accent-gradient-text' : ''}`}>AI Assistant</span>
                )}
              </button>}

            </div>

            {/* ⭐ "?" — the guide of the page on screen, just above Settings (owner,
                2026-10-10). It renders nothing while the page has no guide. */}
            <div className="shrink-0 px-2 pb-1 space-y-1"><PageGuideButton variant="rail" isCollapsed={isCollapsed} />{railButton(settingsItem)}</div>
          </>
        )}

        {/* Sidebar control — the footer slot the wallet used to occupy. */}
        <SidebarControl mode={mode} setMode={setMode} isCollapsed={isCollapsed} />
      </nav>

      {/* Mobile bottom navigation: 2 primary + centre AI + 2 primary */}
      <nav
        aria-label="Primary"
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 safe-area-pb shadow-[0_-4px_16px_-4px_rgba(0,0,0,0.18)]"
      >
        <div className="flex items-stretch justify-around">
          {/* First two primary items */}
          {visibleBottomBar.slice(0, 2).map((item) => (
            <BottomItem key={item.id} item={item} active={active(item.id)} onClick={() => go(item.id)} />
          ))}

          {/* Centre AI assistant button */}
          <div className="flex flex-1 items-center justify-center">
            <button
              type="button"
              onClick={() => setAIOpen(!aiOpen)}
              aria-label="AI Assistant"
              aria-pressed={aiOpen}
              className="-mt-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-4 ring-card transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <AIAssistantIcon className="h-7 w-7" />
            </button>
          </div>

          {/* Last two primary items */}
          {visibleBottomBar.slice(2).map((item) => (
            <BottomItem key={item.id} item={item} active={active(item.id)} onClick={() => go(item.id)} />
          ))}
        </div>
      </nav>

      {/* Mobile left-hand drawer (opened from the Header hamburger) */}
      <NavDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        items={visibleDrawer}
        currentView={currentView}
        onSelect={go}
        isActive={active}
      />
    </>
  );
}

/**
 * The sidebar display control — a footer button that opens a small menu.
 *
 * ⚠️ It is the ONLY way to reach `hover` mode, so it must stay reachable in
 * every mode, including collapsed. Hence icon-only when narrow, never hidden.
 */
function SidebarControl({
  mode, setMode, isCollapsed,
}: { mode: SidebarMode; setMode: (m: SidebarMode) => void; isCollapsed: boolean }) {
  const open = useSidebarStore((s) => s.menuOpen);
  const setOpen = useSidebarStore((s) => s.setMenuOpen);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  // Close on outside click / Escape. ⚠️ Both, not just the click: the menu sits
  // inside a hover-sensitive rail, so a keyboard user needs a way out that does
  // not require moving the pointer.
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = SIDEBAR_MODES.find(m => m.id === mode);

  return (
    <div ref={wrapRef} className="relative shrink-0 border-t border-border p-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        // ⚠️⚠️ ICON ONLY, in BOTH states — so the button needs an accessible
        // name from somewhere. `title` alone is a tooltip, not a name, hence the
        // explicit `aria-label`: an icon-only control with neither is anonymous
        // to a screen reader. The current mode still reaches sighted users via
        // the tooltip and the checked row inside the menu.
        title={`Sidebar: ${current?.label}`}
        aria-label={`Sidebar control — ${current?.label}`}
        className="flex w-full items-center justify-center rounded-md px-2 py-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <PanelLeft className="w-5 h-5 shrink-0" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Sidebar control"
          // ⚠️ Opens UPWARD (bottom-full): the control is pinned to the footer,
          // so a downward menu would render past the rail and be clipped by the
          // nav's own `overflow-hidden`.
          className="absolute bottom-full left-2 z-50 mb-1 min-w-[176px] rounded-lg border border-border bg-card p-1 shadow-lg"
        >
          <p className="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Sidebar control
          </p>
          <div className="my-1 h-px bg-border" />
          {SIDEBAR_MODES.map(m => (
            <button
              key={m.id}
              type="button"
              role="menuitemradio"
              aria-checked={mode === m.id}
              onClick={() => { setMode(m.id); setOpen(false); }}
              className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors ${
                mode === m.id ? 'text-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                mode === m.id ? 'bg-foreground' : 'bg-transparent'
              }`} />
              {m.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WalletRailWidget({ isCollapsed }: { isCollapsed: boolean }) {
  const { setCurrentView } = useCurrentView();
  const balanceCp = useWalletStore((s) => s.balanceCp);

  // ⭐ The wallet is its own view since 2026-08-12, so this is a plain
  // navigation. It used to route to Status and preselect a tab via
  // `status-open-tab` — which meant "you can reach the wallet from the nav" and
  // "the wallet is a Status tab" were the SAME fact, and removing the tab would
  // have broken this button rather than relocating it.
  const openWallet = () => setCurrentView('wallet');

  return (
    <button
      type="button"
      onClick={openWallet}
      /* ⚠️ Where an earned coin flies to. Nothing else in the app had a stable
         handle on this button, and its position is not a constant — the rail
         slides between 64px and 192px — so the reward toast finds it by this
         attribute and MEASURES it at flight time. Renaming it grounds the coin. */
      data-coin-target="wallet"
      title={balanceCp === null ? 'Wallet' : `Wallet — ${formatCoinFull(balanceCp)}`}
      /**
       * ⚠️⚠️ EXPLICIT, because the visible word "Wallet" WAS the accessible
       * name. With the label gone the name would be computed from the contents
       * — the amount's own aria-label — and announce "2 gold · 97 silver · 20
       * copper" with nothing saying what the button opens. `title` does not
       * rescue it: it is only a fallback when there is no content-derived name,
       * and the coin figure supplies one.
       */
      aria-label={balanceCp === null ? 'Wallet' : `Wallet — ${formatCoinFull(balanceCp)}`}
      // ⚠️⚠️ `w-full` is load-bearing. A <button> sizes to its CONTENT even with
      // `display:flex`, so without it the inner `justify-between` has no free
      // space to distribute and the balance sits flush against the label — the
      // "condensed" look. The width now comes from the padded wrapper instead of
      // a margin, because `w-full` + `m-2` would overflow by the margins.
      className={`flex w-full items-center gap-2.5 shrink-0 px-3 py-2 rounded-md border border-border bg-secondary/40 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        isCollapsed ? 'justify-center px-0' : ''
      }`}
    >
      <Coins className="w-5 h-5 shrink-0 text-amber-400" />
      {/* ⭐ The word "Wallet" was dropped (owner, 2026-08-13) — the coin icon
          already says what this is. It also pays for itself: the label was the
          ~40px that pushed the exact breakdown onto a second line, so with it
          gone the full figure fits on ONE again (measured: 107px of amount into
          ~124px available). The two-line stack went with it.
          ⚠️ NOT `compact`. Compact collapses to the dominant denomination, so
          2 gold 97 silver read as "3" — the rail claimed a whole gold that was
          not there. A balance is the one number that must be exact.
          ⚠️ The comment lives HERE, not inside the `&&`: that expression takes
          exactly ONE child, and a comment beside the element is a second one. */}
      {!isCollapsed && (
        <span className="nav-label-in flex-1 flex items-center min-w-0 text-sm font-semibold text-foreground">
          {balanceCp === null ? '…' : <CoinAmount cp={balanceCp} />}
        </span>
      )}
    </button>
  );
}

function BottomItem({
  item,
  active,
  onClick,
}: {
  item: NavDestination;
  active: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={`relative flex flex-1 flex-col items-center justify-center gap-1 min-h-[3.5rem] py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
        active ? 'text-primary nav-active-contrast' : 'text-muted-foreground'
      }`}
    >
      <span className={`flex h-7 w-12 items-center justify-center rounded-full transition-colors ${active ? 'bg-primary/10' : ''}`}>
        <Icon className="w-5 h-5" />
      </span>
      <span className={`text-[11px] font-medium ${active ? 'accent-gradient-text' : ''}`}>{item.label}</span>
      {active && (
        <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full" />
      )}
    </button>
  );
}
